/**
 * reducers/intents.ts
 * desc: client intents
 * note: connects to third-party systems via Y Meadows API endpoints
 *       hierarchy = sources -> directories -> messages -> intents
 */
import type { Reducer } from '@reduxjs/toolkit';
import { sortAz } from '../../utilities';
import type { IntentsAction } from '../actions/intents';
import type { IntentDto } from '../../types/api/intents';
import type { ComponentInfo } from '../../types/api/components';

export interface IntentsState {
    isLoading: {
        createIntent: boolean;
        deleteIntent: boolean;
        getIntent: boolean;
        getIntentUsage: boolean;
        getIntents: boolean;
        updateIntent: boolean;
    };
    // an empty object until an intent is created, fetched or updated
    detail: IntentDto | Record<string, never>;
    list: IntentDto[];
    intentUsage: ComponentInfo[];
}

const INITIAL_STATE: IntentsState = {
    isLoading: {
        createIntent: false,
        deleteIntent: false,
        getIntent: false,
        getIntentUsage: false,
        getIntents: true,
        updateIntent: false,
    },
    detail: {},
    list: [],
    intentUsage: [],
};


function intentsReducer(state: IntentsState = INITIAL_STATE, action: IntentsAction): IntentsState {
    switch (action.type) {
        // CLEAR Review Queues
        case 'CLEAR_INTENTS':
            return _clearIntents();

        // CREATE Intent (Instance)
        case 'CREATE_INTENT_ERROR':
            return _createIntentError(state);
        case 'CREATE_INTENT_REQUEST':
            return _createIntentRequest(state);
        case 'CREATE_INTENT_SUCCESS':
            return _createIntentSuccess(state, action.payload);

        // DELETE Intent (Instance)
        case 'DELETE_INTENT_ERROR':
            return _deleteIntentError(state);
        case 'DELETE_INTENT_REQUEST':
            return _deleteIntentRequest(state);
        case 'DELETE_INTENT_SUCCESS':
            return _deleteIntentSuccess(state, action.payload);

        // GET Intent (Instance)
        case 'GET_INTENT_ERROR':
            return _getIntentError(state);
        case 'GET_INTENT_REQUEST':
            return _getIntentRequest(state);
        case 'GET_INTENT_SUCCESS':
            return _getIntentSuccess(state, action.payload);

        // GET Intent Usage (Instance)
        case 'GET_INTENT_USAGE_ERROR':
            return _getIntentUsageError(state);
        case 'GET_INTENT_USAGE_REQUEST':
            return _getIntentUsageRequest(state);
        case 'GET_INTENT_USAGE_SUCCESS':
            return _getIntentUsageSuccess(state, action.payload);

        // GET Intents (Collection)
        case 'GET_INTENTS_ERROR':
            return _getIntentsError(state);
        case 'GET_INTENTS_REQUEST':
            return _getIntentsRequest(state);
        case 'GET_INTENTS_SUCCESS':
            return _getIntentsSuccess(state, action.payload);

        // Update Intent (Instance)
        case 'UPDATE_INTENT_ERROR':
            return _updateIntentError(state);
        case 'UPDATE_INTENT_REQUEST':
            return _updateIntentRequest(state);
        case 'UPDATE_INTENT_SUCCESS':
            return _updateIntentSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Review Queues   *******************
//*************************************************
function _clearIntents(): IntentsState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CREATE Intent (Instance)   **************
//*************************************************
function _createIntentError(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createIntent: false,
        },
    };
}

function _createIntentRequest(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createIntent: true,
        },
    };
}

function _createIntentSuccess(state: IntentsState, payload: IntentDto): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createIntent: false,
        },
        detail: payload,
        list: sortAz([ ...state.list, payload ], 'name'),
    };
}



//*************************************************
//*****   DELETE Intent (Instance)   **************
//*************************************************
function _deleteIntentError(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteIntent: false,
        },
    };
}

function _deleteIntentRequest(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteIntent: true,
        },
    };
}

function _deleteIntentSuccess(state: IntentsState, intentId: string): IntentsState {
    // create new list without deleted intent
    const [ ...intentsList ] = state.list;
    const deletedIndex = state.list.findIndex(x => x.id === intentId);
    if (deletedIndex > -1) intentsList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteIntent: false,
        },
        list: sortAz(intentsList, 'name'),
    };
}



//*************************************************
//*****   GET Intent (Instance)   *****************
//*************************************************
function _getIntentError(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntent: false,
        },
    };
}

function _getIntentRequest(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntent: true,
        },
    };
}

function _getIntentSuccess(state: IntentsState, payload: IntentDto): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntent: false,
        },
        detail: payload,
    };
}



//*************************************************
//*****   GET Intent Usage (Instance)   ***********
//*************************************************
function _getIntentUsageError(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntentUsage: false,
        },
    };
}

function _getIntentUsageRequest(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntentUsage: true,
        },
    };
}

function _getIntentUsageSuccess(state: IntentsState, payload: ComponentInfo[]): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntentUsage: false,
        },
        intentUsage: payload,
    };
}



//*************************************************
//*****   GET Intents (Collection)   *************
//*************************************************
function _getIntentsError(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntents: false,
        },
    };
}

function _getIntentsRequest(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntents: true,
        },
    };
}

function _getIntentsSuccess(state: IntentsState, payload: IntentDto[]): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getIntents: false,
        },
        list: sortAz(payload, 'name'),
    };
}



//*************************************************
//*****   UPDATE Intent (Instance)   **************
//*************************************************
function _updateIntentError(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateIntent: false,
        },
    };
}

function _updateIntentRequest(state: IntentsState): IntentsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateIntent: true,
        },
    };
}

function _updateIntentSuccess(state: IntentsState, payload: IntentDto): IntentsState {
    let intentsList = [ ...state.list ];

    // if list exists, modify w updated intent
    // note: list will not exist if deeplinked or refreshed detail page
    if (intentsList.length) {
        const updatedIndex = intentsList.findIndex(x => x.id === payload.id);
        if (updatedIndex > -1) intentsList[updatedIndex] = payload;
        intentsList = sortAz(intentsList, 'name');
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateIntent: false,
        },
        detail: payload,
        list: intentsList,
    };
}


export default intentsReducer as Reducer<IntentsState>;
